/**
 * Event density calculation utilities for adaptive calendar display.
 *
 * Determines how much detail to show for events based on event count:
 * - spacious: 0-3 events - full details (location, description, member name, duration)
 * - normal: 4-6 events - standard display (title, time, member color)
 * - compact: 7+ events - minimal display (time + truncated title inline)
 */

import type { CalendarEvent } from '../types/calendar';

export type EventDensity = 'spacious' | 'normal' | 'compact';

export interface DensityThresholds {
  spaciousMax: number; // Max events for spacious (default: 3)
  normalMax: number; // Max events for normal (default: 6)
}

export interface DensityResult {
  density: EventDensity;
  eventCount: number;
  availableHeight?: number;
}

const DEFAULT_THRESHOLDS: DensityThresholds = {
  spaciousMax: 3,
  normalMax: 6,
};

/**
 * Calculate density based on event count.
 */
export function calculateDensity(
  eventCount: number,
  thresholds: DensityThresholds = DEFAULT_THRESHOLDS
): EventDensity {
  if (eventCount <= thresholds.spaciousMax) return 'spacious';
  if (eventCount <= thresholds.normalMax) return 'normal';
  return 'compact';
}

/**
 * Calculate density for a time-based view (Home, Day view).
 * Considers event count and average event height.
 */
export function calculateTimedViewDensity(
  events: CalendarEvent[],
  hourHeight: number,
  options?: {
    minHeightSpacious?: number; // Minimum avg height for spacious (default: 80px)
    minHeightNormal?: number; // Minimum avg height for normal (default: 40px)
    thresholds?: DensityThresholds;
  }
): DensityResult {
  const {
    minHeightSpacious = 80,
    minHeightNormal = 40,
    thresholds = DEFAULT_THRESHOLDS,
  } = options || {};

  // Filter to timed events only (all-day events don't affect density)
  const timedEvents = events.filter((e) => !e.allDay);

  if (timedEvents.length === 0) {
    return { density: 'spacious', eventCount: 0 };
  }

  // First check by event count
  const countBasedDensity = calculateDensity(timedEvents.length, thresholds);

  // Then refine by available height per event
  const avgHeight = calculateAverageEventHeight(timedEvents, hourHeight);

  // Use the more restrictive of count-based or height-based density
  let heightBasedDensity: EventDensity;
  if (avgHeight >= minHeightSpacious) {
    heightBasedDensity = 'spacious';
  } else if (avgHeight >= minHeightNormal) {
    heightBasedDensity = 'normal';
  } else {
    heightBasedDensity = 'compact';
  }

  // Return the more compact of the two
  const densityOrder: EventDensity[] = ['spacious', 'normal', 'compact'];
  const finalDensity =
    densityOrder.indexOf(countBasedDensity) > densityOrder.indexOf(heightBasedDensity)
      ? countBasedDensity
      : heightBasedDensity;

  return {
    density: finalDensity,
    eventCount: timedEvents.length,
    availableHeight: avgHeight,
  };
}

/**
 * Calculate average event height based on duration.
 */
function calculateAverageEventHeight(events: CalendarEvent[], hourHeight: number): number {
  if (events.length === 0) return 0;

  const heights = events.map((event) => {
    const start = new Date(event.startTime);
    const end = new Date(event.endTime);
    const durationHours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    return Math.max(20, durationHours * hourHeight); // Minimum 20px
  });

  return heights.reduce((sum, h) => sum + h, 0) / heights.length;
}

/**
 * Calculate density for a cell-based view (Week, Month).
 */
export function calculateCellDensity(
  events: CalendarEvent[],
  thresholds?: DensityThresholds
): DensityResult {
  const density = calculateDensity(events.length, thresholds);
  return { density, eventCount: events.length };
}

/**
 * Format event duration for display.
 * Examples: "30m", "1h", "1.5h", "2h", "All day"
 */
export function formatEventDuration(event: CalendarEvent): string {
  if (event.allDay) return 'All day';

  const start = new Date(event.startTime);
  const end = new Date(event.endTime);
  const durationMinutes = (end.getTime() - start.getTime()) / (1000 * 60);

  if (durationMinutes < 60) {
    return `${Math.round(durationMinutes)}m`;
  }

  const hours = durationMinutes / 60;
  if (hours === Math.floor(hours)) {
    return `${hours}h`;
  }
  return `${hours.toFixed(1)}h`;
}

/**
 * Get first line of description for snippet display.
 * Strips HTML tags and truncates to maxLength.
 */
export function getDescriptionSnippet(
  description: string | null,
  maxLength: number = 50
): string | null {
  if (!description) return null;

  // Get first line
  const lines = description.split('\n');
  const firstLine = (lines[0] ?? '').trim();

  // Strip HTML tags if present
  const plainText = firstLine.replace(/<[^>]*>/g, '').trim();

  if (!plainText) return null;

  if (plainText.length <= maxLength) return plainText;
  return plainText.slice(0, maxLength - 3) + '...';
}

/**
 * Minimum heights for each density level (in pixels).
 * Used to ensure events remain readable.
 */
export const MIN_EVENT_HEIGHTS: Record<EventDensity, number> = {
  compact: 24, // Just enough for time + title inline
  normal: 32, // Title + time on two lines
  spacious: 60, // Full details
};
